import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { folderService } from "@/services/folderService";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const PRESET_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#64748B",
];

interface CreateFolderModalProps {
  parentFolderId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateFolderModal({
  parentFolderId = null,
  open,
  onOpenChange,
}: CreateFolderModalProps) {
  const { user, organization } = useAuth();
  const [name, setName] = React.useState("");
  const [color, setColor] = React.useState<string | null>(null);
  const [isPrivate, setIsPrivate] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (open) {
      setName("");
      setColor(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id || !user?.id) throw new Error("Missing context");
      const { error } = await folderService.createFolder({
        name: name.trim(),
        parent_folder_id: parentFolderId,
        organization_id: organization.id,
        user_id: user.id,
        is_private: isPrivate,
        folder_color: color,
      } as any);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      onOpenChange(false);
      toast({ title: "Folder created" });
    },
    onError: (err: any) =>
      toast({
        title: "Create failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const canSave = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Private</div>
              <div className="text-xs text-muted-foreground">
                Only you can see this folder
              </div>
            </div>
            <Switch
              checked={isPrivate}
              onCheckedChange={(v) => setIsPrivate(Boolean(v))}
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Color</label>
            <div className="mt-2 grid grid-cols-10 gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  className={`h-6 w-6 rounded border ${color === c ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  aria-label={`Select ${c}`}
                />
              ))}
              <button
                className={`h-6 w-6 rounded border flex items-center justify-center text-[10px] ${!color ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}
                onClick={() => setColor(null)}
                title="No color"
              >
                ×
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSave || createMutation.isPending}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
